import { clsx } from 'clsx'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

import Button from 'Components/Button'
import { BackIcon } from 'Icons'

import type { WheelItemsData, WheelItem } from '../wheelData'

import DetailedDescriptionContent from './DetailedDescriptionContent'
import getParents from './getParents'

type DetailedDescriptionProps = {
  item: WheelItem
  wheelItemsData: WheelItemsData
  onBack?: () => void
}

export default function DetailedDescription({
  item,
  wheelItemsData,
  onBack,
}: DetailedDescriptionProps) {
  const [activeItem, setActiveItem] = useState<WheelItem>(item)
  useEffect(() => {
    setActiveItem(item)
  }, [item])

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex flex-row items-center gap-4">
        {onBack && (
          <Button id="back-button" onClick={onBack}>
            <BackIcon />
            Back
          </Button>
        )}
        <ParentsSection
          child={item}
          activeItem={activeItem}
          wheelItemsData={wheelItemsData}
          setActiveItem={setActiveItem}
        />
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="card card-lg bg-base-300 shadow-sm"
      >
        <div className="card-body">
          <DetailedDescriptionContent item={activeItem} wheelItemsData={wheelItemsData} />
        </div>
      </motion.div>
    </div>
  )
}

type ParentsSectionProps = {
  child: WheelItem
  activeItem: WheelItem
  wheelItemsData: WheelItemsData
  setActiveItem: (item: WheelItem) => void
}

function ParentsSection({ child, activeItem, wheelItemsData, setActiveItem }: ParentsSectionProps) {
  const ancestors = getParents(child, wheelItemsData)
  if (ancestors.length === 0) {
    return null
  }
  return (
    <div className="breadcrumbs py-0.5">
      <ul>
        {ancestors.map((item) => {
          const isActive = activeItem === item
          return (
            <li key={item}>
              <a
                onClick={() => setActiveItem(item)}
                className={clsx('text-lg', { 'font-bold': isActive })}
              >
                {wheelItemsData[item].label.label}
              </a>
            </li>
          )
        })}
        <li>
          <a
            onClick={() => setActiveItem(child)}
            className={clsx('text-lg', { 'font-bold': activeItem === child })}
          >
            {wheelItemsData[child].label.label}
          </a>
        </li>
      </ul>
    </div>
  )
}
